import { useEffect } from 'react';
import { useLoaderData } from '@remix-run/react';
import { LoaderArgs, json, redirect } from '@shopify/remix-oxygen';
import { getProductsByIds } from '~/common/utils/product';
import { getMatchProductGids } from '~/common/utils/metafield';
import { convertProductsToMysteryProducts } from '~/graphql/converters/product';
import { GET_PRODUCTS } from '~/graphql/queries/products';
import { ProductDetails } from '~/sections/product';
import {
  CollectionByHandle,
  CollectionResponse,
} from '~/types/dto/collectionsDto';
import {
  GET_COLLECTIONS,
  buildBatchedCollectionsQuery,
} from '~/graphql/queries/collection';

import { getMetafieldValue } from '~/common/utils/metafield';
import { loadProductPage } from '~/lib/productPage.server';
import {
  isGatedProduct,
  GATED_PRODUCTS_EXCLUDE_QUERY,
} from '~/common/constants/privateSale';
import { Product, ProductMetafields } from '~/types/product';
import useColor from '~/recoil/useColor';
import { colors } from '~/theme/colors';
import { BRAND, SEO, FALLBACK_SITE_URL } from '~/common/constants/seo';
import { Routes } from '~/common/constants/routes';
import { truncateText } from '~/common/utils/string';
import { NotFoundPage } from '~/components/NotFoundPage';
import RegionNotAvailable from '~/components/RegionNotAvailable';

async function loadProductNonMystery(
  productName: string,
  storefront: LoaderArgs['context']['storefront'],
  env: LoaderArgs['context']['env'],
) {
  const siteUrl = env.PUBLIC_SITE_URL || FALLBACK_SITE_URL;

  const notFound = () =>
    json(
      {
        notFound: true as const,
        product: null,
        productName,
        productsByHandle: [],
        matchingByProductId: {},
        collectionsByHandle: [],
        siteUrl,
      },
      { status: 404 },
    );

  const data = await loadProductPage(productName, storefront, {
    isVisible: (product) => !isGatedProduct(product.tags),
  });

  if (!data) return notFound();

  // A private-sale product must not be reachable through its public URL, and a
  // 404 (rather than a redirect) avoids confirming that the handle exists.
  if (isGatedProduct(data.product.tags)) return notFound();

  return json({
    notFound: false as const,
    product: data.product,
    productName,
    productsByHandle: data.productsByHandle,
    matchingByProductId: data.matchingByProductId,
    collectionsByHandle: [],
    siteUrl,
  });
}

async function loadProductMystery(
  productName: string,
  storefront: LoaderArgs['context']['storefront'],
  env: LoaderArgs['context']['env'],
  MYSTERY_COLLECTION_NAME: string,
) {
  try {
    const skipCache = env.DISABLE_LOCALIZATION_CACHE === 'true';
    const cacheStrategy = skipCache
      ? storefront.CacheNone()
      : storefront.CacheShort();

    // Mystery mode needs the full product catalog because convertProductsToMysteryProducts
    // groups products by collection to build composite mystery listings. There's no way
    // to fetch a single mystery product by handle since it's a virtual grouping.
    const [{ products }, { collections }] = await Promise.all([
      storefront.query(GET_PRODUCTS, {
        // Private-sale products are excluded at source so they can never be
        // folded into a mystery grouping or its match recommendations.
        variables: { query: GATED_PRODUCTS_EXCLUDE_QUERY, first: 250 },
        cache: cacheStrategy,
      }),
      storefront.query<CollectionResponse>(GET_COLLECTIONS, {
        variables: {
          query: `title:"%${MYSTERY_COLLECTION_NAME}"`,
          first: 250,
        },
        cache: cacheStrategy,
      }),
    ]);

    const desiredCollectionHandles = collections.edges
      .filter(({ node }) => node.title.includes(MYSTERY_COLLECTION_NAME))
      .map(({ node }) => node.handle);

    let collectionsByHandle: CollectionByHandle[] = [];

    if (desiredCollectionHandles.length > 0) {
      const batchedQuery = buildBatchedCollectionsQuery(
        desiredCollectionHandles,
        50,
      );

      try {
        const batchedResults = await storefront.query(batchedQuery, {
          cache: cacheStrategy,
        });

        collectionsByHandle = Object.values(batchedResults).map(
          (collectionByHandle) => ({
            collectionByHandle,
          }),
        ) as CollectionByHandle[];
      } catch (error) {
        console.error(
          '[loadProductMystery] Failed to fetch batched collections.',
          error,
        );
      }
    }

    const mysteryCollections =
      collections.edges?.filter(({ node }) =>
        node.title.includes(MYSTERY_COLLECTION_NAME),
      ) || [];

    const filteredProducts = convertProductsToMysteryProducts(
      products.edges,
      mysteryCollections,
      MYSTERY_COLLECTION_NAME,
    );

    const productByHandle = filteredProducts.find(
      (product) => product.handle === productName,
    );

    if (!productByHandle) {
      return json(
        {
          notFound: true as const,
          product: null,
          productName,
          productsByHandle: [],
          matchingByProductId: {},
          collectionsByHandle: [],
          siteUrl: env.PUBLIC_SITE_URL || FALLBACK_SITE_URL,
        },
        { status: 404 },
      );
    }

    const productsByHandle = [productByHandle];

    const MAX_MATCHING = 4;
    const matchingByProductId: Record<string, Product[]> = {};

    for (const p of productsByHandle) {
      const gids = getMatchProductGids(p.metafields, ProductMetafields.Match);
      if (!gids.length) {
        matchingByProductId[p.id] = [];
        continue;
      }

      matchingByProductId[p.id] = getProductsByIds(
        filteredProducts,
        gids,
      ).slice(0, MAX_MATCHING);
    }

    return json({
      notFound: false as const,
      product: productByHandle,
      productName,
      productsByHandle,
      matchingByProductId,
      collectionsByHandle: collectionsByHandle || [],
      siteUrl: env.PUBLIC_SITE_URL || FALLBACK_SITE_URL,
    });
  } catch (error) {
    if (error instanceof Response) throw error;
    console.error('[loadProductMystery] Failed to load product.', error);
    throw json({ message: 'Failed to load product' }, { status: 500 });
  }
}

export const loader = async ({
  params: { productName },
  context: { storefront, env },
}: LoaderArgs) => {
  const secretEventHandle = env.PUBLIC_SECRET_EVENT_PRODUCT_HANDLE?.trim();
  if (productName && secretEventHandle && productName === secretEventHandle) {
    throw redirect(Routes.SecretEvent);
  }

  const MYSTERY_COLLECTION_NAME = env.PUBLIC_MYSTERY_COLLECTION_NAME || '';

  if (MYSTERY_COLLECTION_NAME === '') {
    return loadProductNonMystery(productName!, storefront, env);
  }

  return loadProductMystery(
    productName!,
    storefront,
    env,
    MYSTERY_COLLECTION_NAME,
  );
};

export const meta = ({ data }: { data: any }) => {
  const siteUrl = data?.siteUrl || FALLBACK_SITE_URL;

  if (!data?.product) {
    return [
      { title: `Product Not Found | ${SEO.brandName}` },
      { name: 'description', content: SEO.defaultDescription },
    ];
  }

  const product: Product = data.product;
  const metaTitle = product.title
    ? `${product.title} | ${SEO.brandName}`
    : SEO.brandName;
  const metaDescription = truncateText(
    product.seo?.description ||
      getMetafieldValue(
        product.metafields,
        ProductMetafields.MetaDescription,
      ) ||
      product.description ||
      SEO.defaultDescription,
    160,
  );
  const productUrl = product.handle
    ? `${siteUrl}/product/${product.handle}`
    : siteUrl;

  // OG image: prioritize metafield > product image > fallback
  const productImage =
    getMetafieldValue(product.metafields, ProductMetafields.MetaImage) ||
    product.images?.[0];
  const ogImage = productImage
    ? `${productImage}&width=1200&height=630&format=auto&quality=100`
    : SEO.defaultOgImage;

  // Check if any variant is available for sale
  const isAvailable =
    product.variants?.some((v) => v.availableForSale) ?? false;

  // Product structured data for rich results
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: metaDescription,
    url: productUrl,
    image: productImage || `${siteUrl}${SEO.defaultOgImage}`,
    brand: {
      '@type': 'Brand',
      name: BRAND.name,
    },
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: product.prices?.currencyCode || 'GBP',
      price: product.prices?.maxPrice || '0',
      availability: isAvailable
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: BRAND.name,
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Shop',
        item: `${siteUrl}/shop`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: product.title,
        item: productUrl,
      },
    ],
  };

  const metadata = [
    // Primary
    { title: metaTitle },
    { name: 'description', content: metaDescription },
    { name: 'robots', content: 'index, follow' },
    { tagName: 'link', rel: 'canonical', href: productUrl },
    { name: 'referrer', content: 'strict-origin-when-cross-origin' },

    // Open Graph
    { property: 'og:type', content: 'product' },
    { property: 'og:url', content: productUrl },
    { property: 'og:title', content: metaTitle },
    { property: 'og:description', content: metaDescription },
    { property: 'og:image', content: ogImage },
    { property: 'og:site_name', content: SEO.brandName },

    // Twitter
    { name: 'twitter:card', content: SEO.twitterCard },
    { name: 'twitter:title', content: metaTitle },
    { name: 'twitter:description', content: metaDescription },
    { name: 'twitter:image', content: ogImage },

    // Structured Data
    { 'script:ld+json': JSON.stringify([productSchema, breadcrumbSchema]) },
  ];

  // Only specify dimensions for fallback image (we don't know product image's actual size)
  if (!productImage) {
    metadata.push(
      { property: 'og:image:width', content: String(SEO.defaultOgImageWidth) },
      {
        property: 'og:image:height',
        content: String(SEO.defaultOgImageHeight),
      },
    );
  }

  return metadata;
};

export default function ProductRoute() {
  const data = useLoaderData();
  const { setColor } = useColor();

  const product = data.product;

  const metafields = product?.metafields;

  const metaColor = getMetafieldValue(
    metafields,
    ProductMetafields.FlashSaleColor,
  );

  useEffect(() => {
    if (metaColor) {
      setColor(metaColor);
    } else {
      setColor(colors.colorMain);
    }
  }, [metafields]);

  if (data.notFound) {
    return <RegionNotAvailable />;
  }

  return (
    <ProductDetails
      key={product?.id}
      product={product}
      productsByHandle={data.productsByHandle}
      matchingByProductId={data.matchingByProductId}
      collectionsByHandle={data.collectionsByHandle}
    />
  );
}

export function ErrorBoundary() {
  return <NotFoundPage />;
}
